import React from "react";
import ReactDOM from "react-dom";

type Props = {
    x: number;  // Screen X coordinate
    y: number;  // Screen Y coordinate
    stereotype: string;
    defaultName: string;
    onConfirm: (name: string) => void;
    onCancel: () => void;
    mode?: 'create' | 'rename';
};

type State = {
    value: string;
};

// Portal for rendering outside SVG
class NameEditorPortal extends React.Component<{ children: React.ReactNode }> {
    private el: HTMLDivElement;

    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.el = document.createElement("div");
        this.el.className = "fixed top-0 left-0 z-[10000] pointer-events-none";
    }

    componentDidMount() {
        document.body.appendChild(this.el);
    }

    componentWillUnmount() {
        document.body.removeChild(this.el);
    }

    render() {
        return ReactDOM.createPortal(this.props.children, this.el);
    }
}

export default class NameEditor extends React.Component<Props, State> {
    private inputRef = React.createRef<HTMLInputElement>();
    private containerRef = React.createRef<HTMLDivElement>();

    state: State = {
        value: this.props.defaultName
    };

    componentDidMount() {
        // Focus and select the input
        setTimeout(() => {
            if (this.inputRef.current) {
                this.inputRef.current.focus();
                this.inputRef.current.select();
            }
        }, 50);

        // Listen for escape key
        document.addEventListener("keydown", this.handleKeyDown);

        // Delay adding click outside listener to avoid catching the initial click
        setTimeout(() => {
            document.addEventListener("mousedown", this.handleClickOutside, true);
        }, 100);
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this.handleKeyDown);
        document.removeEventListener("mousedown", this.handleClickOutside, true);
    }

    handleClickOutside = (e: MouseEvent) => {
        // Check if click is outside the container
        if (this.containerRef.current && !this.containerRef.current.contains(e.target as Node)) {
            e.preventDefault();
            e.stopPropagation();
            this.props.onCancel();
        }
    };

    handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
            e.preventDefault();
            this.props.onCancel();
        } else if (e.key === "Enter") {
            e.preventDefault();
            this.handleConfirm();
        }
    };

    handleConfirm = () => {
        const name = this.state.value.trim() || this.props.defaultName;
        this.props.onConfirm(name);
    };

    render() {
        const { x, y, stereotype } = this.props;
        const { value } = this.state;

        // Position the editor at screen coordinates, centered horizontally
        const editorWidth = 300;
        const editorHeight = 140;
        const left = Math.max(10, Math.min(x - editorWidth / 2, window.innerWidth - editorWidth - 10));
        const top = Math.max(10, Math.min(y - editorHeight / 2, window.innerHeight - editorHeight - 10));

        return (
            <NameEditorPortal>
                <div
                    ref={this.containerRef}
                    className="fixed bg-[rgba(28,30,36,0.98)] rounded-xl shadow-[0_12px_48px_rgba(0,0,0,0.6),0_0_0_1px_rgba(100,255,180,0.2)] p-4 font-sans backdrop-blur-[16px] pointer-events-auto animate-dialog-fade-in"
                    style={{ left, top, width: editorWidth }}
                >
                    <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-semibold text-accent italic">&laquo;{stereotype}&raquo;</span>
                        <span className="text-xs text-white/50">{this.props.mode === 'rename' ? 'Rename this element' : 'Name this element'}</span>
                    </div>
                    <input
                        ref={this.inputRef}
                        type="text"
                        value={value}
                        onChange={(e) => this.setState({ value: e.target.value })}
                        className="w-full px-3.5 py-3 text-[15px] font-medium bg-white/[0.08] border-2 border-accent/40 rounded-lg text-white outline-none font-sans box-border"
                        placeholder={this.props.defaultName}
                    />
                    <div className="flex justify-end gap-2.5 mt-3">
                        <button
                            className="px-4 py-2 text-[13px] font-medium bg-white/[0.08] border border-white/15 rounded-md text-white/70 cursor-pointer font-sans transition-all duration-150 hover:bg-white/[0.12]"
                            onClick={this.props.onCancel}
                        >
                            Cancel
                        </button>
                        <button
                            className="px-4 py-2 text-[13px] font-medium bg-accent/20 border border-accent/40 rounded-md text-accent cursor-pointer font-sans transition-all duration-150 hover:bg-accent/30"
                            onClick={this.handleConfirm}
                        >
                            {this.props.mode === 'rename' ? 'Rename' : 'Create'}
                        </button>
                    </div>
                    <div className="mt-2.5 text-[11px] text-white/35 text-center">
                        Press <kbd className="bg-white/10 px-1.5 py-0.5 rounded text-[10px] font-sans">Enter</kbd> to confirm, <kbd className="bg-white/10 px-1.5 py-0.5 rounded text-[10px] font-sans">Esc</kbd> to cancel
                    </div>
                </div>
            </NameEditorPortal>
        );
    }
}

export { NameEditor };
